<template>
  <ViewWrapper :carregando="loading" :permissao="PERMISSOES_FGTAS.CADASTRAR_ATENDIMENTO">
    <template #titulo> Cadastro de Atendimentos </template>

    <v-container fluid v-if="!telaBloqueada">
      <v-card flat rounded class="pa-8 mt-4">
        <!-- CARDS INFORMATIVOS -->
        <v-row class="mb-6">
          <v-col cols="12" md="6">
            <v-card class="pa-6 text-center" color="backgroundCardDark" rounded="lg">
              <v-row align="center" justify="center" no-gutters>
                <v-col class="mr-3" cols="auto">
                  <v-icon>mdi-face-agent</v-icon>
                </v-col>
                <v-col cols="auto">
                  <span class="text-h6 font-weight-bold text-no-wrap">Seus Atendimentos Hoje</span>
                </v-col>
                <v-col>
                  <span class="text-h4 mt-2 d-block">{{ quantidadeAtendimentos.totalHoje }}</span>
                </v-col>
              </v-row>
            </v-card>
          </v-col>

          <v-col cols="12" md="6">
            <v-card class="pa-6 text-center" color="backgroundCardDark" rounded="lg">
              <v-row align="center" justify="center" no-gutters>
                <v-col class="mr-3" cols="auto">
                  <v-icon>mdi-face-agent</v-icon>
                </v-col>
                <v-col cols="auto">
                  <span class="text-h6 font-weight-bold text-no-wrap">Total de Atendimentos</span>
                </v-col>
                <v-col>
                  <span class="text-h4 mt-2 d-block">{{ quantidadeAtendimentos.totalGeral }}</span>
                </v-col>
              </v-row>
            </v-card>
          </v-col>
        </v-row>

        <!-- FORMULARIO -->
        <v-form ref="formAtendimento" fast-fail :disabled="loading" @submit.prevent="salvar">
          <v-row>
            <!-- ATENDENTE -->
            <v-col cols="12" md="6">
              <div class="mb-2"><span class="required">*</span> Atendente</div>
              <v-text-field v-model="form.atendente.nome" disabled />
            </v-col>

            <!-- SETOR -->
            <v-col cols="12" md="6">
              <div class="mb-2"><span class="required">*</span> Setor</div>
              <v-text-field v-model="form.setor" disabled />
            </v-col>

            <!-- AGENCIA -->
            <v-col cols="12" md="6" v-if="exibeAgencia">
              <div class="mb-2"><span class="required">*</span> Agência</div>
              <v-select
                v-model="form.agencia"
                :items="listaAgencias"
                item-title="nome"
                :item-value="(item) => item"
                :disabled="listaAgencias.length === 1"
                :rules="[(v) => !!v || 'Selecione uma agência']"
                placeholder="Selecione"
              />
            </v-col>

            <!-- CANAL ATENDIMENTO -->
            <v-col cols="12" md="6">
              <div class="mb-2"><span class="required">*</span> Canal de Atendimento</div>
              <v-select
                v-model="form.canalAtendimento"
                :items="canaisAtendimento"
                item-title="title"
                item-value="value"
                :rules="[(v) => !!v || 'Selecione o tipo']"
                placeholder="Presencial / Online"
              />
            </v-col>
          </v-row>

          <!-- SERVIÇOS -->
          <div class="text-h6 mt-6 mb-3">Serviços</div>

          <v-row>
            <v-col cols="12" md="4" v-for="serv in listaServicos" :key="serv.id">
              <v-checkbox
                v-model="form.servicos"
                :label="serv.nome"
				:color="corTexto"
                :value="serv"
                :rules="[(v) => form.servicos.length > 0 || 'Selecione ao menos um serviço']"
              />
            </v-col>
          </v-row>

          <!-- ALERTA -->
          <v-alert
            type="info"
            color="backgroundCardDark"
            class="mt-4 text-defaultFontColor font-weight-regular"
            v-if="form.servicos.length > 1"
            icon="mdi-alert-circle-outline"
          >
            Atenção! Ao salvar, o sistema irá computar
            <b>{{ form.servicos.length }} atendimentos</b>, pois foram selecionados vários serviços.
          </v-alert>

          <!-- BOTÃO -->
          <v-row class="mt-6">
            <v-col cols="12" class="text-right">
              <v-btn color="primary" type="submit" :loading="loading" class="px-10">
                CONCLUIR
              </v-btn>
            </v-col>
          </v-row>
        </v-form>
      </v-card>
    </v-container>

    <v-container v-else>
      <v-alert type="error" class="mt-6">
        Seu usuário não possui um setor vinculado. Entre em contato com o administrador do sistema
        para regularizar seu cadastro.
      </v-alert>
    </v-container>

    <v-dialog v-model="exibeModal" width="auto" persistent>
      <v-card color="background" class="pa-4" max-width="500" style="border-radius: 10px">
        <template v-slot:title
          ><span :class="`text-${corTexto}`">Solicitação enviada com sucesso!</span></template
        >
        <template v-slot:append>
          <v-btn
            class="text-none"
            :color="corTexto"
            variant="text"
            slim
            icon="mdi-close"
            @click="router.push('/vagas/lista')"
          ></v-btn>
        </template>
        <div class="px-4">Seu atendimento foi cadastrado com sucesso.</div>
        <v-row class="mt-4 mb-2 px-12">
          <v-col cols="12" class="text-center">
            <v-btn block color="primary" text="OK" @click="fecharModal"></v-btn>
          </v-col>
        </v-row>
      </v-card>
    </v-dialog>
  </ViewWrapper>
</template>

<script setup>
import { ref, onMounted, computed, watch } from "vue";
import ViewWrapper from "@/components/commons/wrapper/ViewWrapper.vue";
import {
  buscarQuantidadeAtendimentosUsuario,
  salvarAtendimento,
} from "@/services/AtendimentoService";
import { buscarAgenciasUsuario } from "@/services/AgenciaService";
import { useStore } from "vuex";
import { AtencaoAlert } from "@/utils/AlertUtils.js";
import { PERMISSOES_FGTAS } from "@/utils/PermissaoUtils";
import { vuetify } from '@/plugins/vuetify'

const store = useStore();

// -------------------------------
// CACHE
// -------------------------------
const CACHE_KEY = "cadastro_atendimento_cache";

function loadCache() {
  const cache = localStorage.getItem(CACHE_KEY);
  return cache ? JSON.parse(cache) : null;
}

function saveCache(data) {
  const dadosFiltrados = {
    canalAtendimento: data.canalAtendimento,
    agencia: data.agencia,
  };
  localStorage.setItem(CACHE_KEY, JSON.stringify(dadosFiltrados));
}

// -------------------------------
// ESTADOS
// -------------------------------
const formAtendimento = ref();
const loading = ref(false);
const exibeAgencia = ref(false);
const quantidadeAtendimentos = ref({ totalHoje: 0, totalGeral: 0 });
const usuarioLogado = computed(() => store.getters["usuario/getUsuarioLogado"]);
const exibeModal = ref(false);
const telaBloqueada = ref(false);

const form = ref({
  atendente: { id: null, nome: "" },
  agencia: {},
  setor: null,
  canalAtendimento: "",
  servicos: [],
});

const listaAgencias = ref([]);

const canaisAtendimento = [
  { title: "Presencial", value: "PRESENCIAL" },
  { title: "Online", value: "ONLINE" },
];

const listaServicos = ref([]);

const corTexto = computed(() => {
	return vuetify.theme.current.value.dark ? "white" : "primary";
});

// -------------------------------
// REATIVIDADE
// -------------------------------
watch(
  form,
  (novoValor) => {
    saveCache(novoValor);
  },
  { deep: true }
);

// -------------------------------
// CARREGAMENTO INICIAL
// -------------------------------
onMounted(inicializarTela);

async function inicializarTela() {
  try {
    loading.value = true;
    carregarCache();
    preencherDadosUsuario();
    await Promise.all([carregarQuantidadeAtendimentos(), carregarAgenciasSeNecessario()]);
  } catch (error) {
    console.error("Erro ao carregar tela:", error);
  } finally {
    loading.value = false;
  }
}

function carregarCache() {
  const cache = loadCache();
  if (cache) {
    form.value.canalAtendimento = cache.canalAtendimento || "";
    form.value.agencia = cache.agencia || {};
  }
}

function preencherDadosUsuario() {
  const usuario = usuarioLogado.value;
  form.value.atendente = usuario;

  if (usuario.setor) {
    form.value.setor = usuario.setor.nome;
    listaServicos.value = usuario.setor.servicos || [];
  } else {
    telaBloqueada.value = true;
  }
}

async function carregarQuantidadeAtendimentos() {
  const { data } = await buscarQuantidadeAtendimentosUsuario();
  quantidadeAtendimentos.value = data;
}

async function buscarAgencias() {
  try {
    const { data } = await buscarAgenciasUsuario();
    listaAgencias.value = data;
  } catch (error) {
    console.error(error.response?.data?.mensagem || "Erro ao buscar agências.");
  }
}

async function carregarAgenciasSeNecessario() {
  const setor = usuarioLogado.value.setor?.nome;
  if (setor !== "Agências FGTAS/SINE") return;

  await buscarAgencias();
  const agencias = listaAgencias.value;

  if (agencias.length === 0) {
    console.error("Usuário não possui agências vinculadas.");
  } else if (agencias.length === 1) {
    form.value.agencia = agencias[0];
    exibeAgencia.value = true;
  } else {
    exibeAgencia.value = true;
  }
}

// -------------------------------
// SALVAR
// -------------------------------
async function salvar() {
  const valido = await formAtendimento.value.validate();
  if (!valido) return;

  loading.value = true;
  saveCache(form.value);
  await salvarAtendimento(form.value)
    .then(() => {
      exibeModal.value = true;
      form.value.servicos = [];
      carregarQuantidadeAtendimentos();
    })
    .catch((error) => {
      const errosValidacao = error?.response?.data?.errosValidacao;

      if (errosValidacao) {
        // Extrai todas as mensagens de erro em um array plano
        const mensagens = Object.values(errosValidacao).flat();
        // Exibe todas as mensagens concatenadas ou individualmente
        AtencaoAlert({
          mensagem: mensagens.join("\n"), // ou exibir como lista
          error,
        });
      } else {
        AtencaoAlert({
          mensagem: "Ocorreu um erro ao salvar o atendimento. Tente novamente mais tarde!",
          error,
        });
      }
    })
    .finally(() => {
      loading.value = false;
    });
}
function fecharModal() {
  exibeModal.value = false;
}
</script>

<style scoped>
.required {
  color: red;
  font-weight: bold;
}
</style>
